import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { TfiSave } from 'react-icons/tfi';
import { BiTrash } from 'react-icons/bi';
import { useMutation, useQuery } from 'react-query';
import { bulkUploadQuestions, fetchQuestionsCategories } from 'api/questions';
import { fetchExamsCategories } from 'api/exams';
import { getScenarios } from 'api/scenarios';
import { Modal } from 'components/ui/Modal';
import ReactSelect from 'react-select';

interface Props {
  onClose: () => void;
  refetch: () => void;
}

interface CsvRow {
  [key: string]: string;
}

export const QuestionBulkUpload: React.FC<Props> = ({ onClose, refetch }) => {
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [mappedData, setMappedData] = useState<any[]>([]);
  const { data: categories } = useQuery(['questions-categories'], fetchQuestionsCategories);
  const { data: examsCategories } = useQuery(['exams-categories'], fetchExamsCategories);
  const { data: scenarios } = useQuery(['scenarios-all'], getScenarios);
  const { mutateAsync: bulkUploadMutate, isLoading } = useMutation(bulkUploadQuestions);

  const QUESTION_FIELDS = useMemo(
    () => [
      { label: 'Question', value: 'question', required: true },
      { label: 'Answer A', value: 'answerA' },
      { label: 'Answer B', value: 'answerB' },
      { label: 'Answer C', value: 'answerC' },
      { label: 'Answer D', value: 'answerD' },
      { label: 'Correct Answer', value: 'correctAnswer', required: true },
      { label: 'Category', value: 'category', required: true },
      { label: 'Subcategories', value: 'subcategories' },
      { label: 'Exam Category', value: 'examCategory' },
      { label: 'Scenario', value: 'scenario' },
      { label: 'Answer Explanation / Rationale', value: 'answerExplanation' }
    ],
    []
  );

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedHeaders = results.meta.fields || [];
        setHeaders(parsedHeaders);
        setCsvData(results.data);
        const autoMapping: Record<string, string> = {};
        const mappingRules: Record<string, string[]> = {
          'question': ['question'],
          'answerA': ['answer a', 'answera', 'option a', 'optiona'],
          'answerB': ['answer b', 'answerb', 'option b', 'optionb'],
          'answerC': ['answer c', 'answerc', 'option c', 'optionc'],
          'answerD': ['answer d', 'answerd', 'option d', 'optiond'],
          'correctAnswer': ['correct answer', 'correctanswer', 'correct'],
          'category': ['category 1', 'category1', 'category'],
          'subcategories': ['category 2', 'category2', 'subcategory', 'subcategories'],
          'examCategory': ['exam category', 'examcategory'],
          'scenario': ['scenario'],
          'answerExplanation': ['rationale', 'explanation', 'answer explanation', 'answerexplanation']
        };

        QUESTION_FIELDS.forEach((field) => {
          const rules = mappingRules[field.value] || [field.value.toLowerCase()];
          const found = parsedHeaders.find((h) => {
            const normalizedHeader = h.toLowerCase().replace(/[\s_-]/g, '');
            return rules.some(rule => normalizedHeader.includes(rule.replace(/[\s_-]/g, '')));
          });
          if (found) autoMapping[field.value] = found;
        });

        setFieldMapping(autoMapping);
        setMappedData([]);
        toast.info(`Loaded ${results.data.length} rows and mapped ${Object.keys(autoMapping).length} fields`);
      }
    });
  };

  const handleMappingChange = (appField: string, csvField: string | null) => {
    setFieldMapping((prev) => {
      const newMapping = { ...prev };
      if (csvField === null || csvField === '') {
        delete newMapping[appField];
      } else {
        newMapping[appField] = csvField;
      }
      return newMapping;
    });
  };

  const getAvailableOptions = (currentFieldValue: string) => {
    const usedFields = new Set(Object.values(fieldMapping));

    return headers
      .filter((h) => !usedFields.has(h) || fieldMapping[currentFieldValue] === h)
      .map((h) => ({ label: h, value: h }));
  };

  const generatePreview = () => {
    if (!fieldMapping.question || !fieldMapping.correctAnswer || !fieldMapping.category) {
      toast.error('Question, Category, and Correct Answer are required mappings.');
      return;
    }
    const hasAnswers = ['answerA', 'answerB', 'answerC', 'answerD'].some(
      (key) => fieldMapping[key]
    );

    if (!hasAnswers) {
      toast.error('At least one answer field must be mapped.');
      return;
    }

    const mapped = csvData.map((row, index) => {
      const questionText = row[fieldMapping['question']] || '';
      const categoryName = row[fieldMapping['category']] || '';
      const correctAnswerRaw = row[fieldMapping['correctAnswer']] || '';

      const examCategoryName = fieldMapping['examCategory']
        ? row[fieldMapping['examCategory']]
        : '';
      const scenarioTitle = fieldMapping['scenario']
        ? row[fieldMapping['scenario']]
        : '';
      const subList: string[] = [];
      if (fieldMapping['subcategories']) {
        const raw = row[fieldMapping['subcategories']];
        if (raw) {
          raw.split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((s) => subList.push(s));
        }
      }
      const uniqueSubList = Array.from(new Set(subList));
      const answerA = fieldMapping['answerA'] ? row[fieldMapping['answerA']] || '' : '';
      const answerB = fieldMapping['answerB'] ? row[fieldMapping['answerB']] || '' : '';
      const answerC = fieldMapping['answerC'] ? row[fieldMapping['answerC']] || '' : '';
      const answerD = fieldMapping['answerD'] ? row[fieldMapping['answerD']] || '' : '';
      const validLetters = ['A', 'B', 'C', 'D'];
      const validNumbers = ['1', '2', '3', '4'];
      const validValues = [...validLetters, ...validNumbers];

      const normalizeCorrectAnswer = (value: string) => {
        return value.trim().toUpperCase();
      };

      const normalizedCorrectAnswer = normalizeCorrectAnswer(correctAnswerRaw);

      const isValidCorrectAnswer = validValues.includes(normalizedCorrectAnswer);

      if (!isValidCorrectAnswer && index < 5) {
        console.warn(
          `Row ${index + 1}: Invalid correct answer "${correctAnswerRaw}". Expected A, B, C, D, or 1, 2, 3, 4.`
        );
      }
      const answerExplanation = fieldMapping['answerExplanation']
        ? row[fieldMapping['answerExplanation']] || ''
        : '';

      return {
        question: questionText,
        categoryName,
        subcategories: uniqueSubList,
        examCategoryName,
        scenarioTitle,
        answerA,
        answerB,
        answerC,
        answerD,
        correctAnswer: normalizedCorrectAnswer,
        isValidCorrectAnswer,
        answerExplanation,
      };
    });

    const invalidRows = mapped.filter((row) => !row.isValidCorrectAnswer);
    if (invalidRows.length > 0) {
      toast.warning(`${invalidRows.length} rows have invalid correct answer values. Expected A, B, C, or D.`);
    }

    setMappedData(mapped);
    toast.success(`Preview generated with ${mapped.length} questions`);
  };

  const handleUpload = async () => {
    const invalidRows = mappedData.filter((row) => !row.isValidCorrectAnswer);
    if (invalidRows.length > 0) {
      const proceed = window.confirm(
        `${invalidRows.length} rows have invalid correct answers and will not have any correct option marked. Continue anyway?`
      );
      if (!proceed) return;
    }

    const questionPayloads = mappedData.map((row) => {
      const categoriesArray = [];
      const categoryValue = row.categoryName?.trim();
      if (categoryValue) {
        for (const cat of categories || []) {
          const foundSub = cat.subcategories?.find(sub =>
            sub.toLowerCase().includes(categoryValue.toLowerCase())
          );

          if (foundSub) {
            categoriesArray.push({
              category: cat._id,
              subcategories: [foundSub]
            });
            break;
          }
        }
      }
      for (const subValue of row.subcategories || []) {
        const trimmedSub = subValue.trim();
        for (const cat of categories || []) {
          const foundSub = cat.subcategories?.find(sub =>
            sub.toLowerCase().includes(trimmedSub.toLowerCase())
          );

          if (foundSub) {
            const existing = categoriesArray.find(c => c.category === cat._id);
            if (existing) {
              if (!existing.subcategories.includes(foundSub)) {
                existing.subcategories.push(foundSub);
              }
            } else {
              categoriesArray.push({
                category: cat._id,
                subcategories: [foundSub]
              });
            }
            break;
          }
        }
      }
      const matchedExamCategory = examsCategories?.find(
        (ex) =>
          ex.name.toLowerCase() === row.examCategoryName?.toLowerCase() ||
          ex._id === row.examCategoryName
      )?._id;

      const matchedScenario = scenarios?.result?.find(
        (s) =>
          s.title?.toLowerCase() === row.scenarioTitle?.toLowerCase() ||
          s._id === row.scenarioTitle
      )?._id;

      const letterToIndex: Record<string, number> = {
        A: 0, B: 1, C: 2, D: 3,
        '1': 0, '2': 1, '3': 2, '4': 3,
      };

      const correctIndex = letterToIndex[row.correctAnswer] ?? -1;

      const answers = [
        { answer: row.answerA, correct: correctIndex === 0 },
        { answer: row.answerB, correct: correctIndex === 1 },
        { answer: row.answerC, correct: correctIndex === 2 },
        { answer: row.answerD, correct: correctIndex === 3 },
      ].filter((a) => a.answer);

      return {
        question: row.question,
        answerExplanation: row.answerExplanation,
        isActive: true,
        contentType: 'text',
        answers,
        categories: categoriesArray,
        examCategory: matchedExamCategory || null,
        scenarioId: matchedScenario || null,
      };
    });

    try {
      await bulkUploadMutate(questionPayloads);
      toast.success('Bulk upload complete');
      refetch();
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  return (
    <Modal
      title="Bulk Upload Questions"
      onClose={onClose}
      boxClassName="w-[900px]"
      contentClassName="overflow-y-auto h-[calc(100%-69px)]"
    >
      <div className="mb-6">
        <h3 className="font-semibold text-lg">Upload CSV</h3>
        <input type="file" accept=".csv" onChange={handleFileUpload} />
      </div>

      {headers.length > 0 && (
        <>
          <h3 className="font-semibold text-lg mb-2">Map Fields</h3>
          <p className="text-xs text-gray-500 mb-3">
            Fields marked with * are required. Select "Clear" to remove a mapping.
          </p>

          {QUESTION_FIELDS.map((f) => {
            const availableOptions = getAvailableOptions(f.value);
            const currentValue = fieldMapping[f.value];

            return (
              <div
                key={f.value}
                className="flex items-center justify-between mb-2 gap-2 text-sm"
              >
                <span className="w-1/3 font-medium truncate">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-1">*</span>}
                </span>
                <div className="w-1/2 text-xs">
                  <ReactSelect
                    isClearable
                    placeholder="Select CSV column..."
                    styles={{
                      container: (base) => ({ ...base, minHeight: '28px', fontSize: '12px' }),
                      control: (base) => ({
                        ...base,
                        minHeight: '28px',
                        height: '28px',
                        fontSize: '12px',
                      }),
                      dropdownIndicator: (base) => ({ ...base, padding: '2px' }),
                      clearIndicator: (base) => ({ ...base, padding: '2px' }),
                      valueContainer: (base) => ({ ...base, padding: '0 4px' }),
                      menu: (base) => ({ ...base, fontSize: '12px' }),
                    }}
                    options={availableOptions}
                    value={
                      currentValue
                        ? { label: currentValue, value: currentValue }
                        : null
                    }
                    onChange={(v) => handleMappingChange(f.value, v?.value ?? null)}
                  />
                </div>
              </div>
            );
          })}

          <button type="button" className="secondaryBtn mt-4" onClick={generatePreview}>
            Generate Preview
          </button>
        </>
      )}

      {mappedData.length > 0 && (
        <div className="overflow-x-auto mb-6 mt-4">
          <h3 className="font-semibold text-lg mb-3">
            Preview Data ({mappedData.length} questions)
          </h3>
          {mappedData.some((row) => !row.isValidCorrectAnswer) && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded mb-3 text-xs">
              Some rows have invalid correct answer values. They are highlighted in red.
            </div>
          )}

          <table className="table-auto w-full border text-xs">
            <thead>
              <tr>
                <th className="border p-1 bg-gray-100">#</th>
                {Object.keys(mappedData[0])
                  .filter((key) => key !== 'isValidCorrectAnswer')
                  .map((key) => (
                    <th key={key} className="border p-1 bg-gray-100">
                      {key}
                    </th>
                  ))}
                <th className="border p-1 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappedData.map((row, i) => (
                <tr
                  key={i}
                  className={!row.isValidCorrectAnswer ? 'bg-red-50' : ''}
                >
                  <td className="border p-1 text-center">{i + 1}</td>
                  {Object.keys(row)
                    .filter((key) => key !== 'isValidCorrectAnswer')
                    .map((key) => (
                      <td
                        key={key}
                        className={`border p-1 ${key === 'correctAnswer' && !row.isValidCorrectAnswer
                          ? 'bg-red-200'
                          : ''
                          }`}
                      >
                        <input
                          type="text"
                          className="w-full border-none outline-none text-xs bg-transparent"
                          value={
                            Array.isArray(row[key])
                              ? row[key].join(', ')
                              : typeof row[key] === 'string'
                                ? row[key]
                                : JSON.stringify(row[key])
                          }
                          onChange={(e) => {
                            const newData = [...mappedData];
                            newData[i][key] = e.target.value;
                            if (key === 'correctAnswer') {
                              const validLetters = ['A', 'B', 'C', 'D'];
                              const validNumbers = ['1', '2', '3', '4'];
                              const validValues = [...validLetters, ...validNumbers];
                              const normalizeCorrectAnswer = (value: string) => {
                                return value.trim().toUpperCase();
                              };

                              const trimmedValue = normalizeCorrectAnswer(e.target.value);
                              newData[i].isValidCorrectAnswer = validValues.includes(trimmedValue);
                              newData[i].correctAnswer = trimmedValue;
                            }

                            setMappedData(newData);
                          }}
                        />
                      </td>
                    ))}
                  <td className="border p-1 text-center">
                    <BiTrash
                      className="text-danger w-4 h-4 cursor-pointer mx-auto"
                      onClick={() =>
                        setMappedData(mappedData.filter((_, idx) => idx !== i))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mappedData.length > 0 && (
        <div className="flex gap-3">
          <button
            type="button"
            className="secondaryBtn"
            onClick={handleUpload}
            disabled={isLoading}
          >
            <TfiSave />
            {isLoading ? 'Uploading...' : `Upload All (${mappedData.length})`}
          </button>
          <button type="button" className="secondaryBtn" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}
    </Modal>
  );
};